<app-page [showBackButton]="true" title="New License">
  <form (ngSubmit)="createLicense()" [formGroup]="licenseForm" class="grid p-fluid">
    <!-- Actions Bar -->
    <div class="col-12">
      <div class="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 border-300">
        <h2 class="m-0 text-lg font-semibold">License Information</h2>
        <div class="flex gap-2">
          <button
            pButton
            type="submit"
            class="p-button-sm"
            [disabled]="
              licenseForm.invalid ||
              (!isGrantingMode() && licenseNumberValid() === false) ||
              (!isGrantingMode() && isCoaterLicense && lineExtensions.length === 0)
            "
            data-testid="create-license-button"><i class="pi pi-save" pButtonIcon></i><span pButtonLabel>{{ isGrantingMode() ? 'Create Granting License' : 'Save' }}</span></button>
        </div>
      </div>
    </div>

    <!-- License Type Selection -->
    <div class="field mb-2 col-12 md:col-6">
      <app-select
        [label]="'License Type'"
        [options]="licenseTypeOptions"
        [required]="true"
        id="licenseType"
        testId="licenseType"></app-select>
    </div>

    <!-- License Number Input -->
    @if (nextLicenseNumber) {
      <div class="field mb-2 col-12 md:col-6">
        <label for="licenseNumber" class="font-medium text-900">
          License Number
          @if (!isCoaterLicense) {
            <span class="text-red-500">*</span>
          }
        </label>
        <p-inputgroup>
          @if (licensePrefix || nextLicenseNumber.usesDynamicPrefix) {
            <p-inputgroup-addon>{{ licensePrefix || 'XX' }}-</p-inputgroup-addon>
          }
          <input
            pInputText
            type="number"
            formControlName="licenseNumber"
            id="licenseNumber"
            min="1"
            (blur)="onLicenseNumberBlur()"
            class="flex-1"
            data-testid="licenseNumber" />
          @if (checkingNumber()) {
            <p-inputgroup-addon>
              <i class="pi pi-spin pi-spinner"></i>
            </p-inputgroup-addon>
          }
          @if (!checkingNumber() && licenseNumberValid() === true) {
            <p-inputgroup-addon>
              <i class="pi pi-check text-green-500"></i>
            </p-inputgroup-addon>
          }
          @if (!checkingNumber() && licenseNumberValid() === false) {
            <p-inputgroup-addon>
              <i class="pi pi-times text-red-500"></i>
            </p-inputgroup-addon>
          }
        </p-inputgroup>
        @if (licenseNumberError()) {
          <small class="p-error">{{ licenseNumberError() }}</small>
        }
        @if (isCoaterLicense && !isGrantingMode()) {
          <small class="text-500">To create a granting license (placeholder), clear the license number.</small>
        }
      </div>
    }

    <!-- Organisation Selection -->
    <div class="field mb-2 col-12 md:col-6">
      <app-autocomplete
        (itemSelected)="organisationSelected($event)"
        (searchQueryChanged)="searchOrganisation($event)"
        [optionLabel]="'name'"
        [required]="true"
        [suggestions]="organisations"
        id="organisation"
        label="Coater / Organisation"
        testId="organisation"></app-autocomplete>
    </div>

    <!-- Production Site Selection (for Coater and Decorator licenses) -->
    @if ((isCoaterLicense || isDecoratorLicense) && productionSiteOptions.length > 1) {
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'Production Site'"
          [options]="productionSiteOptions"
          [required]="true"
          id="productionSiteId"
          testId="productionSite"></app-select>
      </div>
    } @else if ((isCoaterLicense || isDecoratorLicense) && productionSiteOptions.length === 1) {
      <div class="field mb-2 col-12 md:col-6">
        <label class="font-medium text-900">Production Site</label>
        <div class="production-site-display p-2 border-round surface-100">
          {{ productionSiteOptions[0]?.label }}
        </div>
      </div>
    }

    <!-- Active Licenses Warning -->
    @if (showActiveLicensesWarning) {
      <div class="col-12">
        <p-message severity="warn" class="mb-3">
          <span>
            This production site already has {{ activeLicenses.length }} active license(s). Creating a new license when
            an active license exists may lead to duplicates and poor data quality. <br />
            Please verify if a renewal of the existing license is more appropriate before proceeding.
          </span>
        </p-message>
      </div>
    }

    <!-- Granting License Info Banner -->
    @if (isGrantingMode()) {
      <div class="col-12">
        <p-message severity="info" class="mb-3">
          <span>
            No license number or dates provided — a granting license (placeholder) will be created. Number, dates, and
            line extensions will be assigned when the license is activated.
          </span>
        </p-message>
      </div>
    }

    <!-- First Certification Year -->
    @if (!isGrantingMode()) {
      <div class="field mb-2 col-12 md:col-6">
        <app-select
          [label]="'First Certification Year'"
          [options]="yearOptions"
          [required]="!isCoaterLicense"
          id="certificationYear"
          testId="certificationYear"></app-select>
      </div>

      <!-- Date of Granting -->
      <div class="field mb-2 col-12 md:col-3">
        <app-calendar
          label="Date of Granting"
          id="issueDate"
          [required]="!isCoaterLicense"
          testId="issueDate"></app-calendar>
      </div>

      <!-- Expiry Date (read-only, calculated) -->
      <div class="field mb-2 col-12 md:col-3">
        <label class="font-medium text-900">Expiry Date</label>
        <div class="expiry-date-display">
          {{ $safeNavigationMigration(licenseForm.get('expiryDate')?.value) | date: 'dd.MM.yyyy' : 'UTC' }}
        </div>
      </div>
    }

    <!-- Line Extensions (only for Coater License with selected production site, not in granting mode) -->
    @if (isCoaterLicense && hasSelectedProductionSite && !isGrantingMode()) {
      <div class="col-12 mb-2">
        <app-line-extensions-selector
          [coatingLines]="coatingLines"
          (lineExtensionsChange)="onLineExtensionsChange($event)"></app-line-extensions-selector>
      </div>
    }
  </form>
</app-page>
